import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Async, AsyncComponent } from '@mona/shared/utils';
import { PipDialogService } from './pip-dialog.service';

/**
 * Reason for dialog Close
 */
export enum PipCallDialogComponentCloseReason {
    HANG_UP = 'HANG_UP',
    FULL_SCREEN = 'FULL_SCREEN',
}

/**
 * Picture in picture call dialog
 */
@Async()
@Component({
    selector: 'app-pip-call-dialog',
    templateUrl: './pip-call-dialog.component.html',
    styleUrls: ['./pip-call-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class PipCallDialogComponent extends AsyncComponent implements AfterViewInit {
    /**
     * Holds a reference to the remote video element
     */
    @ViewChild('remoteVideo') remoteVideo: ElementRef<HTMLVideoElement>;

    /**
     * Holds a reference to the local video element
     */
    @ViewChild('localVideo') localVideo: ElementRef<HTMLVideoElement>;

    /**
     * Is mic enabled
     */
    microphoneEnabled = this.data.microphoneEnabled;

    /**
     * Is video enabled
     */
    videoEnabled = this.data.videoEnabled;

    /**
     * Is remote video disabled
     */
    remoteVideoDisabled = this.data.remoteVideoDisabled;

    /**
     * Is remote audio disabled
     */
    remoteAudioDisabled = this.data.remoteAudioDisabled;

    /**
     * Remote participant display name
     */
    remoteParticipantName = this.data.remoteParticipantName;

    /**
     * Local participant display name
     */
    localParticipantName = this.data.localParticipantName;

    /**
     * URL to call component
     */
    callUrl = '/telemedicine/call';

    /**
     * Has Call Partner
     */
    hasCallPartner = this.data.hasCallPartner;

    /**
     * Is screen capture
     */
    isScreenCapture = this.data.isScreenCapture;

    /**
     * Volume (0–10), same key as fullscreen call UI
     */
    volume = localStorage.getItem('call-volume') ? +localStorage.getItem('call-volume') : 5;

    private readonly volumeStorageKey = 'call-volume';

    /**
     * constructor
     * @param dialogRef MatDialogRef<PipCallDialogComponent>
     * @param pipDialogService PipDialogService
     * @param data Any
     * @param router Router
     * @param route ActivatedRoute
     */
    constructor(
        public dialogRef: MatDialogRef<PipCallDialogComponent>,
        public pipDialogService: PipDialogService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            videoEnabled: boolean;
            microphoneEnabled: boolean;
            remoteVideoDisabled: boolean;
            remoteAudioDisabled: boolean;
            remoteParticipantName: string;
            localParticipantName: string;
            hasCallPartner: boolean;
            isScreenCapture: boolean;
        },
        private router: Router,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    /**
     * NgHook
     */
    ngAfterViewInit() {
        // "Mount" remote video stream
        this.async(
            this.pipDialogService.setRemoteStream.subscribe(
                stream => (this.remoteVideo.nativeElement.srcObject = stream),
            ),
        );

        // "Mount" local video stream
        this.async(
            this.pipDialogService.setLocalStream.subscribe(
                stream => (this.localVideo.nativeElement.srcObject = stream),
            ),
        );

        // Close on hangup from outside
        this.async(
            this.pipDialogService.hungUp.subscribe(() => {
                this.dialogRef.close();
            }),
        );

        // Change to icon when no calling partner is there
        this.async(
            this.pipDialogService.hasCallPartnerChange.subscribe(hasCallPartner => {
                this.hasCallPartner = hasCallPartner;
                this.cdr.markForCheck();
            }),
        );

        // Handle screen capture start / end
        this.async(
            this.pipDialogService.isScreenCaptureChange.subscribe(isScreenCapture => {
                this.isScreenCapture = isScreenCapture;
                this.cdr.markForCheck();
            }),
        );

        // Remote video toggled
        this.async(
            this.pipDialogService.remoteVideoToggled.subscribe(value => {
                this.remoteVideoDisabled = value;
                this.cdr.markForCheck();
            }),
        );

        // Remote audio toggled
        this.async(
            this.pipDialogService.remoteAudioToggled.subscribe(value => {
                this.remoteAudioDisabled = value;
                this.cdr.markForCheck();
            }),
        );

        // Local camera / mic state — single source of truth from CallWebrtcService
        this.async(
            this.pipDialogService.localVideoToggled.subscribe(value => {
                this.videoEnabled = value;
                this.cdr.markForCheck();
            }),
        );

        this.async(
            this.pipDialogService.localMicrophoneToggled.subscribe(value => {
                this.microphoneEnabled = value;
                this.cdr.markForCheck();
            }),
        );

        // Participant names
        this.async(
            this.pipDialogService.participantNamesChange.subscribe(({ remote, local }) => {
                this.remoteParticipantName = remote;
                this.localParticipantName = local;
                this.cdr.markForCheck();
            }),
        );

        // Volume change
        this.async(
            this.pipDialogService.volumeChange.subscribe(volume => {
                this.volume = volume;
                this.cdr.markForCheck();
            }),
        );
    }

    /**
     * Hang up call — service tears down and closes dialog with HANG_UP reason.
     */
    hangUp() {
        this.pipDialogService.requestHangUp.emit();
    }

    /**
     * Back to fullscreen
     */
    fullscreen() {
        if (this.isAtCallUrl()) {
            this.dialogRef.close(PipCallDialogComponentCloseReason.FULL_SCREEN);
        } else {
            this.router.navigateByUrl(this.callUrl).then(() => {
                this.dialogRef.close(PipCallDialogComponentCloseReason.FULL_SCREEN);
            });
        }
    }

    /**
     * Returns true if navigated to call url
     */
    isAtCallUrl() {
        return this.router.url === this.callUrl;
    }

    /**
     * Toggles the microphone. UI state comes back via localMicrophoneToggled.
     */
    toggleMicrophone() {
        this.pipDialogService.toggleMic.emit();
    }

    /**
     * Toggles the video. UI state comes back via localVideoToggled (avoid double-flip).
     */
    toggleVideo() {
        this.pipDialogService.toggleCamera.emit();
    }

    /**
     * Starts screen sharing
     */
    startScreenCapture() {
        this.pipDialogService.startScreenCapture.emit();
    }

    /**
     * Stops screen sharing
     */
    stopScreenCapture() {
        this.pipDialogService.stopScreenCapture.emit();
    }

    /**
     * Changes volume by step and persists
     */
    changeVolume(difference: number): void {
        this.volume = Math.min(10, Math.max(0, this.volume + difference));
        this.saveVolume();
    }

    /**
     * Saves volume for PIP video + fullscreen call sync
     */
    saveVolume(): void {
        localStorage.setItem(this.volumeStorageKey, `${this.volume}`);
        this.pipDialogService.volumeChange.emit(this.volume);
    }
}
